<template>
  <section class="auditor-report">
    <b-card class="auditor-filters">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
        <div>
          <h2 class="mb-50">
            Auditor
          </h2>
          <p class="text-muted mb-0">
            Review inventory, asset values, and purchase references across branches.
          </p>
        </div>
        <b-badge
          variant="light-primary"
          class="mt-1 mt-md-0"
        >
          Current records
        </b-badge>
      </div>
      <b-tabs
        v-model="tab"
        @input="resetReport"
      >
        <b-tab title="Item Stock" />
        <b-tab title="Assets" />
      </b-tabs>
      <b-alert
        show
        variant="info"
        class="mt-2 mb-2"
      >
        <div class="p-1">
          <strong>{{ tab === 0 ? 'Stock valuation basis' : 'Asset valuation basis' }}</strong>
          <p class="mb-0 mt-50">
            {{ tab === 0
              ? 'Latest PO unit price across all branches, before discounts and taxes. Estimated value = available quantity × PO price; this is not FIFO or average costing.'
              : 'Recorded asset values without currency conversion. Includes unconfirmed assets and disposal requests.' }}
            Unavailable values are shown as —.
          </p>
        </div>
      </b-alert>
      <b-form @submit.prevent="resetReport">
        <b-row class="align-items-end">
          <b-col
            lg="3"
            md="6"
          >
            <b-form-group
              label="Branch"
              label-for="auditor-branch"
            >
              <v-select-new
                v-model="branch"
                input-id="auditor-branch"
                :options="branches"
                label="label"
                :reduce="option => option.value"
                :clearable="false"
                placeholder="All branches"
                :disabled="branchesLoading"
                @input="resetReport"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="tab === 0"
            lg="3"
            md="6"
          >
            <b-form-group
label="Category"
label-for="auditor-category"
>
              <v-select-new
                v-model="category"
                input-id="auditor-category"
                :options="categories"
                :reduce="option => option.value"
                :clearable="false"
                :disabled="categoriesLoading"
                @input="resetReport"
              />
              <small
v-if="categoryError"
class="text-danger"
>
                {{ categoryError }}
                <b-button
size="sm"
variant="link"
@click="fetchCategories"
>Try again</b-button>
              </small>
            </b-form-group>
          </b-col>
          <b-col
            :lg="tab === 0 ? 3 : 6"
            md="6"
          >
            <b-form-group
              label="Search records"
              label-for="auditor-search"
            >
              <b-form-input
                id="auditor-search"
                v-model="search"
                maxlength="200"
                :placeholder="tab === 0 ? 'Item name, code, branch, or shelf' : 'Item name, code, branch, asset or serial number'"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="3"
            class="mb-1"
          >
            <div class="auditor-actions">
              <b-button
                type="submit"
                variant="primary"
                :disabled="loading"
              >
                Apply filters
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="loading"
                @click="clearFilters"
              >
                Reset
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-form>
      <b-alert
        :show="!!branchError"
        variant="warning"
      >
        <div class="p-1">
          {{ branchError }} <b-button
            size="sm"
            variant="outline-warning"
            @click="fetchBranches"
          >
            Try again
          </b-button>
        </div>
      </b-alert>
    </b-card>
    <b-card
      no-body
      class="auditor-results"
    >
      <div class="auditor-toolbar p-2">
        <div>
          <h4 class="mb-50">
            {{ tab === 0 ? 'Item Stock' : 'Assets' }}
          </h4>
          <span
            class="text-muted"
            aria-live="polite"
          >{{ loading ? 'Loading report…' : rangeLabel }}</span>
        </div>
        <div class="auditor-actions">
          <label
            for="auditor-page-size"
            class="mb-0 text-muted"
          >Rows</label>
          <b-form-select
            id="auditor-page-size"
            v-model="perPage"
            :options="[10, 20, 30, 50, 100, { value: 'all', text: 'All' }]"
            :disabled="loading"
            class="auditor-page-size"
            @change="resetReport"
          />
          <b-button
            variant="outline-primary"
            :disabled="exporting || loading || !loaded || !!error || total === 0"
            @click="exportExcel"
          >
            <b-spinner
              v-if="exporting"
              small
              class="mr-50"
            />
            {{ exporting ? 'Exporting…' : 'Export Excel' }}
          </b-button>
        </div>
      </div>
      <b-alert
        :show="!!error"
        variant="danger"
        class="mx-2"
      >
        <div class="p-1">
          {{ error }} <b-button
            size="sm"
            variant="outline-danger"
            @click="fetchRows"
          >
            Try again
          </b-button>
        </div>
      </b-alert>
      <b-alert
        :show="!!exportError"
        variant="danger"
        class="mx-2"
      >
        <div class="p-1">
          {{ exportError }}
        </div>
      </b-alert>
      <b-table
        :items="rows"
        :fields="fields"
        :busy="loading"
        primary-key="id"
        sticky-header="65vh"
        responsive
        hover
        show-empty
        class="auditor-table mb-0"
      >
        <template #cell()="data">
          {{ display(data.value, data.field.key) }}
        </template>
        <template #cell(item_name)="data">
          <span class="auditor-item-name">{{ display(data.value, 'item_name') }}</span>
        </template>
        <template #cell(confirmed)="data">
          <b-badge :variant="data.value === 'Yes' ? 'light-success' : 'light-warning'">
            {{ data.value === 'Yes' ? 'Confirmed' : 'Pending' }}
          </b-badge>
        </template>
        <template #cell(disposed)="data">
          <b-badge :variant="data.value === 'Yes' ? 'light-warning' : 'light-secondary'">
            {{ data.value === 'Yes' ? 'Requested' : 'None' }}
          </b-badge>
        </template>
        <template #table-busy>
          <div class="text-center p-4">
            <b-spinner
              small
              class="mr-50"
            /> Loading report…
          </div>
        </template>
        <template #empty>
          <div class="text-center p-4">
            <h5>{{ error ? 'Report unavailable' : 'No matching records' }}</h5>
            <p class="text-muted mb-0">
              {{ error ? 'Please try loading the report again.' : 'Try another branch or adjust your search terms.' }}
            </p>
          </div>
        </template>
      </b-table>
      <div class="auditor-toolbar p-2 border-top">
        <small class="text-muted">Excel includes all matching records across every page.</small>
        <b-pagination
          v-if="perPage !== 'all'"
          v-model="page"
          :total-rows="total"
          :per-page="perPage"
          :disabled="loading || !!error"
          :limit="3"
          first-number
          last-number
          class="mb-0"
          aria-label="Auditor report pagination"
          @change="changePage"
        />
      </div>
    </b-card>
  </section>
</template>

<script>
import axios from 'axios'
import moment from 'moment-timezone'
import {
  BCard, BTabs, BTab, BAlert, BRow, BCol, BForm, BFormGroup, BFormSelect,
  BFormInput, BButton, BTable, BSpinner, BPagination, BBadge,
} from 'bootstrap-vue'
import apiEndpoints from '@/constants/apiEndpoints'

const stockFields = [
  ['branch_name', 'Branch'], ['item_code', 'Item Code'], ['item_name', 'Item Name'],
  ['quantity', 'Qty'], ['price', 'Unit Price'], ['currency', 'Currency'], ['stock_value', 'Estimated Stock Value'],
  ['unit', 'Unit'], ['shelf', 'Shelf'], ['category', 'Category'],
  ['price_reference', 'PO Reference'], ['price_date', 'Price Date (Jakarta)'],
]
const assetFields = [
  ['branch_name', 'Current Branch'], ['asset_number', 'Asset Number'], ['item_name', 'Item Name'],
  ['acquisition_value', 'Acquisition Value'], ['residual_value', 'Residual Value'], ['market_value', 'Market Value'],
  ['item_code', 'Item Code'], ['category', 'Category'], ['serial_number', 'Serial Number'],
  ['location', 'Location'], ['condition', 'Condition'], ['status', 'Status'],
  ['confirmed', 'Confirmation'], ['disposed', 'Disposal Request'], ['acquisition_date', 'Acquisition Date'],
]
const numbers = ['quantity', 'price', 'stock_value', 'acquisition_value', 'residual_value', 'market_value']
const xlsxType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export default {
  components: {
    BCard,
    BTabs,
    BTab,
    BAlert,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BButton,
    BTable,
    BSpinner,
    BPagination,
    BBadge,
  },
  data() {
    return {
      tab: 0,
      branch: '',
      search: '',
      appliedSearch: '',
      page: 1,
      perPage: 10,
      category: '',
      categories: [{ value: '', label: 'All categories' }],
      categoriesLoading: false,
      categoryError: '',
      rows: [],
      total: 0,
      branches: [{ value: '', label: 'All branches' }],
      branchesLoading: false,
      branchError: '',
      loading: false,
      loaded: false,
      exporting: false,
      error: '',
      exportError: '',
      requestId: 0,
    }
  },
  computed: {
    type() { return this.tab === 0 ? 'stocks' : 'assets' },
    fields() {
      return (this.tab === 0 ? stockFields : assetFields).map(([key, label]) => ({
        key,
        label,
        thClass: numbers.includes(key) ? 'text-right' : '',
        tdClass: numbers.includes(key) ? 'text-right text-nowrap auditor-number' : '',
      }))
    },
    rangeLabel() {
      if (!this.loaded || this.error) return 'Report unavailable'
      if (!this.total) return '0 records'
      const from = this.perPage === 'all' ? 1 : (this.page - 1) * this.perPage + 1
      return `${from}–${from + this.rows.length - 1} of ${this.total.toLocaleString('en-US')} records`
    },
  },
  mounted() {
    this.fetchBranches()
    this.fetchCategories()
    this.fetchRows()
  },
  beforeDestroy() { this.requestId += 1 },
  methods: {
    url(path) { return `${String(process.env.VUE_APP_API_URL || '').replace(/\/$/, '')}/${apiEndpoints.API_AUDITOR}${path}` },
    headers() { return { Authorization: `Bearer ${this.$cookies.get('userToken')}` } },
    params() {
      return {
        branch_id: this.branch, search: this.appliedSearch, category: this.tab === 0 ? this.category : '', limit: this.perPage, offset: this.perPage === 'all' ? 0 : (this.page - 1) * this.perPage,
      }
    },
    display(value, key) {
      if (value === null || value === undefined || value === '') return '—'
      if (numbers.includes(key)) return Number(value).toLocaleString('en-US', { minimumFractionDigits: key === 'quantity' ? 0 : 2, maximumFractionDigits: 2 })
      if (key === 'price_date') return moment.utc(value).tz('Asia/Jakarta').locale('en').format('DD MMM YYYY, HH:mm')
      return value
    },
    clearFilters() {
      this.branch = ''
      this.category = ''
      this.search = ''
      this.resetReport()
    },
    changePage(page) {
      this.page = page
      this.fetchRows()
    },
    resetReport() {
      this.appliedSearch = this.search.trim()
      this.page = 1
      this.exportError = ''
      this.fetchRows()
    },
    async fetchCategories() {
      this.categoriesLoading = true
      this.categoryError = ''
      try {
        const { data } = await axios.get(this.url('categories'), { headers: this.headers() })
        if (!data.success || !Array.isArray(data.data)) throw new Error('Categories failed')
        this.categories = [{ value: '', label: 'All categories' }, ...data.data.map(value => ({ value, label: value }))]
      } catch (e) {
        this.categoryError = 'Unable to load categories.'
      } finally {
        this.categoriesLoading = false
      }
    },
    async fetchBranches() {
      this.branchesLoading = true
      this.branchError = ''
      try {
        const { data } = await axios.get(this.url('branches'), { headers: this.headers() })
        if (!data.success || !Array.isArray(data.data)) throw new Error('Branches failed')
        this.branches = [{ value: '', label: 'All branches' }, ...data.data.map(b => ({
          value: b.branch_id,
          label: `${b.branch_code} — ${b.branch_name}`,
        }))]
      } catch (e) {
        this.branchError = 'Unable to load branches.'
      } finally {
        this.branchesLoading = false
      }
    },
    async fetchRows() {
      this.requestId += 1
      const id = this.requestId
      this.loading = true
      this.loaded = false
      this.error = ''
      this.rows = []
      try {
        const { data } = await axios.get(this.url(this.type), { headers: this.headers(), params: this.params() })
        if (id !== this.requestId) return
        if (!data.success || !Array.isArray(data.data) || !Number.isInteger(data.data_count) || data.data_count < 0) throw new Error('Report failed')
        const lastPage = this.perPage === 'all' ? 1 : Math.max(1, Math.ceil(data.data_count / this.perPage))
        if (this.page > lastPage) {
          this.page = lastPage
          await this.fetchRows()
          return
        }
        this.rows = data.data
        this.total = data.data_count
        this.loaded = true
      } catch (e) {
        if (id === this.requestId) this.error = 'Unable to load the report. Check your access or try again.'
      } finally {
        if (id === this.requestId) this.loading = false
      }
    },
    async exportExcel() {
      if (this.exporting || this.loading || !this.loaded || this.error || !this.total) return
      this.exporting = true
      this.exportError = ''
      const { type } = this
      try {
        const response = await axios.get(this.url(type), {
          headers: this.headers(), params: { ...this.params(), export: 'xlsx' }, responseType: 'blob',
        })
        if (!String(response.headers['content-type'] || '').includes(xlsxType)) throw new Error('Invalid export response')
        const url = URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = `auditor-${type}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (e) {
        this.exportError = 'Export failed. Check your login session or try again.'
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.auditor-filters, .auditor-results { border: 1px solid rgba(128, 128, 128, 0.16); box-shadow: 0 4px 20px rgba(34, 41, 47, 0.04); }
.auditor-report h2 { font-weight: 700; letter-spacing: -0.025em; }
.auditor-report ::v-deep .nav-tabs { margin-top: 1.5rem; border-bottom: 1px solid rgba(128, 128, 128, 0.18); }
.auditor-report ::v-deep .nav-tabs .nav-link { padding: 0.9rem 1.5rem; font-weight: 600; }
.auditor-report ::v-deep .alert-info { background: rgba(0, 169, 204, 0.06); border-left: 3px solid #00a9cc; }
.auditor-report ::v-deep .alert-info p { line-height: 1.65; font-size: 0.9rem; }
.auditor-report ::v-deep .auditor-table { margin-bottom: 0; scrollbar-width: thin; }
.auditor-report ::v-deep .auditor-table th { white-space: nowrap; font-size: 0.75rem; letter-spacing: 0.035em; padding: 1rem 1.25rem; }
.auditor-report ::v-deep .auditor-table td { padding: 0.95rem 1.25rem; font-size: 0.9rem; }
.auditor-item-name { display: block; min-width: 230px; max-width: 360px; font-weight: 500; line-height: 1.5; }
.auditor-report ::v-deep .auditor-table td:nth-child(2) { white-space: nowrap; }
.auditor-toolbar, .auditor-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 0.75rem; }
.auditor-toolbar { justify-content: space-between; }
.auditor-page-size { width: 80px; }
.auditor-report ::v-deep .auditor-table th { min-width: 100px; }
.auditor-report ::v-deep .auditor-table td { min-width: 100px; vertical-align: middle; }
.auditor-report ::v-deep .auditor-table td:nth-child(3) { min-width: 200px; }
.auditor-report ::v-deep .auditor-number { font-variant-numeric: tabular-nums; }
@media (max-width: 575px) {
  .auditor-toolbar { align-items: flex-start; flex-direction: column; }
  .auditor-toolbar .auditor-actions { width: 100%; }
}
</style>
